import { create } from 'zustand'

import { getQuestsApi } from '@api'
import { QuestId } from '@store'

import { createSelectors, Mapper } from './helpers'

interface SubQuestMap {
  slug: QuestId
}

export const BONUS_QUESTS_MAPS: Record<number, SubQuestMap[]> = {
  0: [{ slug: QuestId.EyeGate }, { slug: QuestId.NotQuest }],
  3: [{ slug: QuestId.Hotey }],
  5: [{ slug: QuestId.Lock }],
  7: [{ slug: QuestId.Expansion }],
  10: [{ slug: QuestId.Duality }],
  13: [{ slug: QuestId.Void }],
  15: [{ slug: QuestId.Moment }],
  18: [{ slug: QuestId.Call }],
}

export const MINOR_QUESTS_MAPS: Record<number, SubQuestMap[]> = {
  1: [{ slug: QuestId.Camera }],
  4: [{ slug: QuestId.CameraCabinet }],
  6: [{ slug: QuestId.CameraPanelka }],
  9: [{ slug: QuestId.CameraOscilloscope }],
  12: [{ slug: QuestId.CameraMirror }],
}

const NOT_MAJOR_QUEST_MAP = [BONUS_QUESTS_MAPS, MINOR_QUESTS_MAPS].reduce<
  Partial<Record<QuestId, number>>
>((acc, map) => {
  Object.entries(map).forEach(([page, quests]) => {
    quests.forEach((q) => {
      acc[q.slug] = Number(page)
    })
  })
  return acc
}, {})

export interface QuestShortDetailsBackend {
  id: number
  slug: string
  image: string
  type: 'major' | 'prologue' | 'bonus' | 'minor'
  is_solved: boolean
  is_available: boolean
  stars_collected: number
  stars_total: number
}

export interface QuestShortDetails {
  id: number
  slug: string
  type: 'major' | 'prologue' | 'bonus' | 'minor'
  image: string
  isSolved: boolean
  isAvailable: boolean
  starsCollected: number
  starsTotal: number
}

const AvailableQuestMapper: Mapper<
  QuestShortDetailsBackend,
  QuestShortDetails
> = {
  mapFromBackend: function (data) {
    return {
      id: data.id,
      isSolved: data.is_solved,
      isAvailable: data.is_available,
      image: data.image,
      slug: data.slug,
      type: data.type,
      starsCollected: data.stars_collected,
      starsTotal: data.stars_total,
    }
  },
  mapToBackend(data) {
    return {
      id: data.id,
      is_solved: data.isSolved,
      is_available: data.isAvailable,
      stars_collected: data.starsCollected,
      stars_total: data.starsTotal,
      image: data.image,
      slug: data.slug,
      type: data.type,
    }
  },
}

type Pages = {
  current: number
  total: number
}

interface AvailableQuestState {
  isLoading: boolean
  isError: boolean
  quests: QuestShortDetails[]
  isLanguageSelection: boolean
  pages: Pages
  actions: {
    getAvailableQuests: () => void
    setPages: (data: Pages) => void
    setIsLanguageSelection: (data: boolean) => void
  }
}

export const QUEST_ITEMS_PER_PAGE = 3
export const MAX_AVAILABLE_PAGE = 19

const useQuestStoreBase = create<AvailableQuestState>((set) => ({
  isLoading: true,
  isError: false,
  quests: [],
  isLanguageSelection: true,
  pages: {
    current: 0,
    total: 0,
  },
  actions: {
    setIsLanguageSelection: (data: boolean) => {
      set(() => ({ isLanguageSelection: data }))
    },
    getAvailableQuests: async () => {
      set(() => ({ isLoading: true }))

      const { data, ok } = await getQuestsApi()

      if (ok && data) {
        const mapped = data.map((item) =>
          AvailableQuestMapper.mapFromBackend(item)
        )

        const prologueQuests = mapped.filter((item) => item.type === 'prologue')
        const isLanguageSelection = prologueQuests.some((item) => item.isSolved)
        set(() => {
          let lastQuestInfo: { slug: string; isAlive: boolean } | null = null
          try {
            const info = JSON.parse(
              localStorage.getItem('last-quest-slug') ?? '{}'
            )
            if (info.slug && info.createdAt) {
              lastQuestInfo = {
                slug: info.slug,
                isAlive: Date.now() - info.createdAt < 24 * 60 * 60 * 1000,
              }
            }
          } catch (e) {
            console.error('Error due parse last quest info', e)
          }

          const isLastQuestNotMajorId =
            lastQuestInfo && lastQuestInfo.isAlive
              ? NOT_MAJOR_QUEST_MAP[lastQuestInfo.slug as QuestId]
              : null
          const majorQuests = mapped.filter((q) => q.type === 'major')

          let currentPage = 0
          if (typeof isLastQuestNotMajorId === 'number') {
            currentPage = isLastQuestNotMajorId
          } else {
            const lastSolvedQuestIndex =
              majorQuests.findLastIndex((q) =>
                lastQuestInfo?.isAlive
                  ? q.slug === lastQuestInfo.slug
                  : q.isSolved
              ) + 1

            currentPage = Math.max(
              0,
              Math.ceil(lastSolvedQuestIndex / QUEST_ITEMS_PER_PAGE) - 1
            )
          }

          const maxTotalPages = MAX_AVAILABLE_PAGE + 1
          const calculatedTotal = Math.ceil(
            majorQuests.length / QUEST_ITEMS_PER_PAGE
          )
          const total = Math.min(calculatedTotal, maxTotalPages)
          return {
            isLanguageSelection,
            quests: mapped,
            pages: {
              current: currentPage,
              total,
            },
          }
        })
      }

      set(() => ({
        isLoading: false,
      }))
    },
    setPages: (data: Pages) => {
      set(() => {
        const limitedCurrent = Math.min(
          Math.max(data.current, 0),
          MAX_AVAILABLE_PAGE
        )
        return {
          pages: {
            current: limitedCurrent,
            total: data.total,
          },
        }
      })
    },
  },
}))

export const useAvailableQuestsActions = () =>
  useQuestStoreBase((state) => state.actions)
export const useAvailableQuestsState = () =>
  useQuestStoreBase((state) => state.quests)
export const useAvailableQuestsIsLanguageSelection = () =>
  useQuestStoreBase((state) => state.isLanguageSelection)
export const useAvailableQuestsIsLoading = () =>
  useQuestStoreBase((state) => state.isLoading)
export const usePageQuestState = () => useQuestStoreBase((state) => state.pages)

export const useAvailableQuests = createSelectors(useQuestStoreBase)
